import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  Mail,
  Newspaper,
  Plus,
  Send,
  Trash2,
  WifiOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  MarketingFilterPanel,
  MarketingFilterSearch,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { ActionIconButton } from './components/ActionIconButton';
import { OutletEmailDialog } from './components/OutletEmailDialog';
import { StatusBadge, DeleteConfirmDialog, EmptyState } from './components/marketing-ui';
import {
  FieldHint,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import {
  useContentQuery,
  useCreatePlacementMutation,
  useDeletePlacementMutation,
  usePlacementsQuery,
  useTransitionPlacementMutation,
  useUpdatePlacementMutation,
} from '@/client/api/marketing/studio';
import { useMediaOutletsQuery } from '@/client/api/marketing/sources';
import { usePlaysQuery } from '@/client/api/marketing/plays';
import type { ApiPlacement } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  DISTRIBUTION_STATUS_BADGE,
  DISTRIBUTION_STATUS_LABEL,
  MEDIUM_LABEL,
  type DistributionStatus,
  type TraditionalMedium,
} from '@/client/types/marketing';

/**
 * Studio → Traditional, on real data.
 *
 * The one place in Studio with a `published` action, and the reason is worth
 * stating: a social post publishes itself when the worker claims it, but we only
 * *pitch* a placement. Whether it runs is the outlet's decision, so somebody has
 * to record that it did — along with the URL, which is what turns the placement
 * into coverage.
 *
 * As on Social, there is no bulk "mark as". Status moves through submit →
 * approve → published, each of which is a distinct fact rather than a field.
 */

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const STATUS_FILTERS: (DistributionStatus | 'all')[] = [
  'all',
  'draft',
  'pending_approval',
  'queued',
  'scheduled',
  'published',
  'failed',
];

type PressSort = 'scheduled' | 'published' | 'outlet' | 'status';

const STATUS_RANK: Record<string, number> = {
  draft: 0,
  pending_approval: 1,
  queued: 2,
  scheduled: 3,
  publishing: 4,
  published: 5,
  failed: 6,
};

const toDatetimeLocal = (iso?: string | null) => (iso ? iso.slice(0, 16) : '');
const fromDatetimeLocal = (value: string) => (value ? new Date(value).toISOString() : null);

interface FormState {
  moid: string;
  medium: TraditionalMedium;
  scheduledAt: string;
  mciid: string;
  playId: string;
  notes: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const emptyForm: FormState = {
  moid: '',
  medium: 'online_news',
  scheduledAt: '',
  mciid: '',
  playId: '',
  notes: '',
};

const toForm = (p: ApiPlacement): FormState => ({
  moid: String(p.moid),
  medium: p.medium as TraditionalMedium,
  scheduledAt: toDatetimeLocal(p.scheduled_at),
  mciid: p.mciid != null ? String(p.mciid) : '',
  playId: p.play_id != null ? String(p.play_id) : '',
  notes: p.notes ?? '',
});

const DistributionTraditionalPage = () => {
  const { data, isLoading, isError, error } = usePlacementsQuery({ limit: 200 });
  const outlets = useMediaOutletsQuery({ limit: 200 });
  const contentQ = useContentQuery({ limit: 200 });
  const plays = usePlaysQuery({ limit: 100 });

  const createPlacement = useCreatePlacementMutation();
  const updatePlacement = useUpdatePlacementMutation();
  const deletePlacement = useDeletePlacementMutation();
  const transition = useTransitionPlacementMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ApiPlacement | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [deleting, setDeleting] = useState<ApiPlacement | null>(null);
  const [publishing, setPublishing] = useState<ApiPlacement | null>(null);
  const [emailing, setEmailing] = useState<ApiPlacement | null>(null);
  const [composing, setComposing] = useState(false);
  const [publishUrl, setPublishUrl] = useState('');
  const [publishUrlError, setPublishUrlError] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<DistributionStatus | 'all'>('all');
  const [contentFilter, setContentFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [mediumFilter, setMediumFilter] = useState<TraditionalMedium | 'all'>('all');
  const [playFilter, setPlayFilter] = useState('all');
  const [sort, setSort] = useState<PressSort>('scheduled');
  const [search, setSearch] = useState('');

  const placements: ApiPlacement[] = data?.items ?? [];
  const playList = plays.data?.items ?? [];
  const contentList = contentQ.data?.items ?? [];

  /** Outlets we may pitch: active, marked as destinations, not do-not-pitch. */
  const destinationOutlets = useMemo(
    () =>
      (outlets.data?.items ?? []).filter(
        (o) => o.active && o.use_as_destination && !o.do_not_pitch,
      ),
    [outlets.data],
  );
  const outletName = (moid: number) =>
    outlets.data?.items.find((o) => o.moid === moid)?.name ?? `Outlet ${moid}`;
  const contentTitle = (mciid: number | null) =>
    mciid == null ? null : (contentList.find((c) => c.mciid === mciid)?.title ?? null);
  const playName = (id: number | null) =>
    playList.find((p) => p.play_id === id)?.name ?? null;

  useEffect(() => {
    if (dialogOpen) {
      setForm(editing ? toForm(editing) : emptyForm);
      setFormErrors({});
    }
  }, [dialogOpen, editing]);

  const patchForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((e) => clearFieldError(e, key));
  };

  const insights = useMemo(() => {
    const pipeline = placements.filter(
      (p) => p.status === 'queued' || p.status === 'scheduled',
    ).length;
    const published = placements.filter((p) => p.status === 'published').length;
    const pending = placements.filter((p) => p.status === 'pending_approval').length;
    const outletCount = new Set(placements.map((p) => p.moid)).size;
    return { pipeline, published, pending, outlets: outletCount };
  }, [placements]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = placements.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (contentFilter === 'linked' && p.mciid == null) return false;
      if (contentFilter === 'unlinked' && p.mciid != null) return false;
      if (mediumFilter !== 'all' && p.medium !== mediumFilter) return false;
      if (playFilter === 'none' && p.play_id != null) return false;
      if (playFilter !== 'all' && playFilter !== 'none' && String(p.play_id) !== playFilter) {
        return false;
      }
      if (q) {
        const hay = [
          outletName(p.moid),
          MEDIUM_LABEL[p.medium as TraditionalMedium] ?? p.medium,
          p.status,
          contentTitle(p.mciid) ?? '',
          playName(p.play_id) ?? '',
          p.url ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    list.sort((a, b) => {
      switch (sort) {
        case 'outlet':
          return outletName(a.moid).localeCompare(outletName(b.moid));
        case 'status':
          return (
            (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9) ||
            outletName(a.moid).localeCompare(outletName(b.moid))
          );
        case 'published':
          return (b.published_at ?? '').localeCompare(a.published_at ?? '');
        case 'scheduled':
        default:
          return (b.scheduled_at ?? b.published_at ?? '').localeCompare(
            a.scheduled_at ?? a.published_at ?? '',
          );
      }
    });
    return list;
  }, [
    placements,
    statusFilter,
    contentFilter,
    mediumFilter,
    playFilter,
    search,
    sort,
    outlets.data,
    contentList,
    playList,
  ]);

  const statusFilterOptions = useMemo(
    () =>
      STATUS_FILTERS.map((s) => ({
        value: s,
        label: s === 'all' ? 'All statuses' : DISTRIBUTION_STATUS_LABEL[s],
      })),
    [],
  );

  const mediumFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All mediums' },
      ...(Object.keys(MEDIUM_LABEL) as TraditionalMedium[]).map((m) => ({
        value: m,
        label: MEDIUM_LABEL[m],
      })),
    ],
    [],
  );

  const playFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All plays' },
      { value: 'none', label: 'No play' },
      ...playList.map((p) => ({ value: String(p.play_id), label: p.name })),
    ],
    [playList],
  );

  const contentFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All content links' },
      { value: 'linked', label: 'Linked to content' },
      { value: 'unlinked', label: 'Not linked' },
    ],
    [],
  );

  const sortOptions = useMemo(
    () => [
      { value: 'scheduled', label: 'Sort: Target date' },
      { value: 'published', label: 'Sort: Published' },
      { value: 'outlet', label: 'Sort: Outlet' },
      { value: 'status', label: 'Sort: Status' },
    ],
    [],
  );

  const filtersActive =
    statusFilter !== 'all' ||
    mediumFilter !== 'all' ||
    playFilter !== 'all' ||
    contentFilter !== 'all' ||
    sort !== 'scheduled' ||
    search.trim().length > 0;

  const clearFilters = () => {
    setStatusFilter('all');
    setMediumFilter('all');
    setPlayFilter('all');
    setContentFilter('all');
    setSort('scheduled');
    setSearch('');
  };

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (placement: ApiPlacement) => {
    setEditing(placement);
    setDialogOpen(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};
    if (!form.moid) {
      errors.moid = 'Pick an outlet to pitch';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      return;
    }
    const outlet = destinationOutlets.find((o) => String(o.moid) === form.moid);
    const payload = {
      moid: Number(form.moid),
      // Take the medium from the outlet when we know it: an outlet's medium is a
      // fact about the outlet, not a per-placement choice.
      medium: (outlet?.medium as TraditionalMedium) ?? form.medium,
      scheduled_at: fromDatetimeLocal(form.scheduledAt),
      mciid: form.mciid ? Number(form.mciid) : null,
      play_id: form.playId ? Number(form.playId) : null,
      notes: form.notes.trim() || null,
    };

    const handlers = {
      onSuccess: () => {
        toast.success(editing ? 'Placement updated' : 'Placement added');
        setDialogOpen(false);
      },
      onError: (err: unknown) =>
        toast.error(getApiErrorMessage(err, 'Could not save the placement')),
    };

    if (editing) updatePlacement.mutate({ mtpid: editing.mtpid, ...payload }, handlers);
    else createPlacement.mutate(payload, handlers);
  };

  const confirmPublished = () => {
    if (!publishing) return;
    const url = publishUrl.trim();
    if (url && !/^https?:\/\//i.test(url)) {
      setPublishUrlError('The URL must start with http:// or https://');
      toast.error('Fix the highlighted fields before saving');
      return;
    }
    setPublishUrlError(undefined);
    transition.mutate(
      { mtpid: publishing.mtpid, action: 'published', url: url || undefined },
      {
        onSuccess: () => {
          toast.success('Marked as published');
          setPublishing(null);
          setPublishUrl('');
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not mark it published')),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deletePlacement.mutate(
      { mtpid: deleting.mtpid },
      {
        onSuccess: () => toast.success('Placement removed'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove the placement')),
      },
    );
    setDeleting(null);
  };

  return (
    <MarketingPageShell
      title="Traditional"
      description="Press and broadcast placements. We pitch — the outlet decides whether it runs, so publication is recorded, not assumed."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Studio' },
        { label: 'Traditional' },
      ]}
      icon={Newspaper}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="gap-2" onClick={() => setComposing(true)}>
            <Mail className="h-4 w-4" /> Email outlet
          </Button>
          <Button type="button" className="gap-2" onClick={openAdd}>
            <Plus className="h-4 w-4" /> New placement
          </Button>
        </div>
      }
      stats={[
        { key: 'pipeline', label: 'In pipeline', value: insights.pipeline },
        { key: 'pending', label: 'Pending approval', value: insights.pending },
        { key: 'published', label: 'Published', value: insights.published },
        { key: 'outlets', label: 'Outlets', value: insights.outlets },
      ]}
    >
      <MarketingFilterPanel
        summary={marketingMatchSummary(visible.length, placements.length, 'placement', isLoading)}
        showClear={filtersActive}
        onClear={clearFilters}
      >
        <MarketingFilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search outlet, content, play, URL…"
        />
        <MarketingFilterSelect
          options={statusFilterOptions}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as DistributionStatus | 'all')}
          placeholder="All statuses"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={mediumFilterOptions}
          value={mediumFilter}
          onChange={(value) => setMediumFilter(value as TraditionalMedium | 'all')}
          placeholder="All mediums"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={playFilterOptions}
          value={playFilter}
          onChange={setPlayFilter}
          placeholder="All plays"
          clearValue="all"
          loading={plays.isLoading}
          className={`${marketingFilterSelectClass} sm:min-w-[12rem]`}
        />
        <MarketingFilterSelect
          options={contentFilterOptions}
          value={contentFilter}
          onChange={(value) => setContentFilter(value as typeof contentFilter)}
          placeholder="All content links"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={sortOptions}
          value={sort}
          onChange={(value) => setSort(value as PressSort)}
          placeholder="Sort"
          clearValue="scheduled"
          className={marketingFilterSelectClass}
        />
      </MarketingFilterPanel>

      {isLoading ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading placements…
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load placements</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(error, 'The marketing API did not respond')}
          </div>
        </div>
      ) : placements.length === 0 ? (
        <EmptyState label="No placements yet. Use Email outlet to ask an outlet for its rates or pitch a story; the placement is created for you." />
      ) : visible.length === 0 ? (
        <EmptyState label="No placements match these filters." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <TooltipProvider delayDuration={200}>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2.5 font-medium">Outlet</th>
                <th className="px-3 py-2.5 font-medium">Medium</th>
                <th className="px-3 py-2.5 font-medium">Content</th>
                <th className="px-3 py-2.5 font-medium">Play</th>
                <th className="px-3 py-2.5 font-medium">Target / published</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((p) => {
                const title = contentTitle(p.mciid);
                return (
                  <tr key={p.mtpid} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        className="font-medium text-gray-900 underline-offset-2 hover:underline"
                      >
                        {outletName(p.moid)}
                      </button>
                      {p.notes && (
                        <p className="line-clamp-1 text-[11px] text-gray-400">{p.notes}</p>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                      {MEDIUM_LABEL[p.medium as TraditionalMedium] ?? p.medium}
                    </td>
                    <td className="max-w-[14rem] px-3 py-2.5 text-xs">
                      {title ? (
                        <Link
                          to={`/admin/marketing/studio/content/${p.mciid}`}
                          className="line-clamp-2 text-blue-700 hover:underline"
                        >
                          {title}
                        </Link>
                      ) : (
                        <span className="text-gray-400">Not linked</span>
                      )}
                    </td>
                    <td className="max-w-[8rem] px-3 py-2.5 text-xs text-gray-600">
                      {playName(p.play_id) ?? <span className="text-gray-400">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                      {p.published_at ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" />
                          {new Date(p.published_at).toLocaleDateString()}
                        </span>
                      ) : p.scheduled_at ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          {new Date(p.scheduled_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-gray-400">no date</span>
                      )}
                      {p.url && (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-1 inline-flex text-blue-700"
                          title={p.url}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusBadge
                        label={
                          DISTRIBUTION_STATUS_LABEL[p.status as DistributionStatus] ?? p.status
                        }
                        className={
                          DISTRIBUTION_STATUS_BADGE[p.status as DistributionStatus] ?? ''
                        }
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        {p.status === 'draft' && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1"
                            onClick={() =>
                              transition.mutate(
                                { mtpid: p.mtpid, action: 'submit' },
                                {
                                  onSuccess: () => toast.success('Submitted for approval'),
                                  onError: (err) =>
                                    toast.error(getApiErrorMessage(err, 'Could not submit')),
                                },
                              )
                            }
                          >
                            <Send className="h-3.5 w-3.5" /> Submit
                          </Button>
                        )}
                        {(p.status === 'queued' || p.status === 'scheduled') && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1"
                            onClick={() => {
                              setPublishing(p);
                              setPublishUrl(p.url ?? '');
                              setPublishUrlError(undefined);
                            }}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> It ran
                          </Button>
                        )}
                        {p.status === 'pending_approval' && (
                          <Link
                            to="/admin/marketing/crew/approvals"
                            className="text-xs text-blue-700 hover:underline"
                          >
                            Approvals →
                          </Link>
                        )}
                        <ActionIconButton label="Email the outlet" onClick={() => setEmailing(p)}>
                          <Mail className="h-4 w-4" />
                        </ActionIconButton>
                        <ActionIconButton
                          label="Delete"
                          onClick={() => setDeleting(p)}
                          className="text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </ActionIconButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TooltipProvider>
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
            {visible.length} shown. Marking a placement published records the URL and turns it
            into owned coverage in Radar.
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit placement' : 'New placement'}</DialogTitle>
            <DialogDescription>
              A placement is a pitch to one outlet. Recording that it ran is a separate step,
              because that is the outlet's decision rather than ours.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} noValidate className="space-y-4">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Outlet <RequiredMark />
              </span>
              <select
                value={form.moid}
                onChange={(e) => patchForm('moid', e.target.value)}
                className={fieldClass(inputClass, formErrors.moid)}
                aria-invalid={!!formErrors.moid}
              >
                <option value="">Select an outlet…</option>
                {destinationOutlets.map((o) => (
                  <option key={o.moid} value={String(o.moid)}>
                    {o.name} · {MEDIUM_LABEL[o.medium as TraditionalMedium] ?? o.medium}
                  </option>
                ))}
              </select>
              <FieldHint error={formErrors.moid} />
              {destinationOutlets.length === 0 && !outlets.isLoading && (
                <span className="text-[11px] text-amber-700">
                  No pitchable outlets. Mark one as a destination under Sources → Outlets, and
                  check it isn’t flagged do-not-pitch.
                </span>
              )}
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Target date</span>
                <input
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                  className={inputClass}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Play</span>
                <select
                  value={form.playId}
                  onChange={(e) => setForm((f) => ({ ...f, playId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">No play linked</option>
                  {playList.map((p) => (
                    <option key={p.play_id} value={String(p.play_id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Content</span>
              <select
                value={form.mciid}
                onChange={(e) => setForm((f) => ({ ...f, mciid: e.target.value }))}
                className={inputClass}
              >
                <option value="">Not linked to a content item</option>
                {contentList.map((c) => (
                  <option key={c.mciid} value={String(c.mciid)}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Notes</span>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="min-h-[64px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
                placeholder="Angle, journalist, embargo, what they asked for…"
              />
            </label>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={createPlacement.isPending || updatePlacement.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createPlacement.isPending || updatePlacement.isPending}
              >
                {createPlacement.isPending || updatePlacement.isPending
                  ? 'Saving…'
                  : editing
                    ? 'Save changes'
                    : 'Add placement'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!publishing}
        onOpenChange={(v) => {
          if (!v) {
            setPublishing(null);
            setPublishUrl('');
            setPublishUrlError(undefined);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>It ran?</DialogTitle>
            <DialogDescription>
              Record that {publishing ? outletName(publishing.moid) : 'this outlet'} published it.
              The URL is what turns this placement into coverage — without one it counts, but
              nobody can read it.
            </DialogDescription>
          </DialogHeader>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">
              Published URL <span className="font-normal text-gray-400">(recommended)</span>
            </span>
            <input
              value={publishUrl}
              onChange={(e) => {
                setPublishUrl(e.target.value);
                setPublishUrlError(undefined);
              }}
              className={fieldClass(inputClass, publishUrlError)}
              aria-invalid={!!publishUrlError}
              placeholder="https://…"
            />
            <FieldHint error={publishUrlError} />
          </label>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setPublishing(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmPublished} disabled={transition.isPending}>
              {transition.isPending ? 'Saving…' : 'Mark published'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OutletEmailDialog
        open={composing}
        placement={null}
        outlet={undefined}
        onClose={() => setComposing(false)}
      />

      <OutletEmailDialog
        open={!!emailing}
        placement={placements.find((x) => x.mtpid === emailing?.mtpid) ?? emailing}
        outlet={emailing ? outlets.data?.items.find((o) => o.moid === emailing.moid) : undefined}
        onClose={() => setEmailing(null)}
      />

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete placement?"
        description={
          deleting
            ? `The pitch to ${outletName(deleting.moid)} will be removed. Any coverage already recorded from it stays.`
            : ''
        }
        onConfirm={confirmDelete}
      />
    </MarketingPageShell>
  );
};

export default DistributionTraditionalPage;
